import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import ContactModal from './ContactModal';

const Header = ({ showNav = true, onChatClick }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [closeTimeout, setCloseTimeout] = useState(null);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);
  const location = useLocation();

  const megaMenuData = {
    business: {
      title: 'Business',
      sections: [
        {
          title: 'Start Your Business',
          links: [
            { name: 'Register Your Business', path: '/services/register-business' },
            { name: 'Limited Liability Company (LLC)', path: '/services/llc' },
            { name: 'Corporation (C-Corp / S-Corp)', path: '/services/corporation' },
            { name: 'Doing Business As (DBA)', path: '/services/dba' },
            { name: 'Nonprofit', path: '/services/nonprofit' },
            { name: 'Sole Proprietorship', path: '/services/sole-proprietorship' },
          ],
        },
        {
          title: 'Run Your Business',
          links: [
            { name: 'Business Compliance Coverage', path: '/services/compliance' },
            { name: 'Annual Report Filing', path: '/services/annual-report' },
            { name: 'Business Licenses', path: '/services/licenses' },
            { name: 'Operating Agreement', path: '/services/operating-agreement' },
            { name: 'Registered Agent', path: '/services/registered-agent' },
            { name: 'Virtual Mail', path: '/services/virtual-mail' },
            { name: 'Business Dissolution', path: '/services/dissolution' },
            { name: 'Business Credit', path: '/services/business-credit' },
          ],
        },
        {
          title: 'Protect Your Business',
          links: [
            { name: 'Trademark Registration', path: '/services/trademark' },
            { name: 'Comprehensive Trademark Search', path: '/services/trademark-search' },
            { name: 'Free Trademark Search', path: '/services/free-trademark' },
            { name: 'Trademark Monitoring', path: '/services/trademark-monitoring' },
            { name: 'Provisional Patent', path: '/services/patent' },
            { name: 'Copyright Registration', path: '/services/copyright' },
          ],
        },
      ],
    },
    personal: {
      title: 'Personal',
      sections: [
        {
          title: 'Estate Planning',
          links: [
            { name: 'Will', path: '/services/will' },
            { name: 'Trust', path: '/services/trust' },
          ],
        },
        {
          title: 'Family Law',
          links: [
            { name: 'Power of Attorney', path: '/services/power-of-attorney' },
            { name: 'Healthcare Directive', path: '/services/healthcare-directive' },
            { name: 'Divorce', path: '/services/divorce' },
          ],
        },
        {
          title: 'Real Estate',
          links: [
            { name: 'Residential Lease', path: '/services/residential-lease' },
            { name: 'Property Deed Transfer', path: '/services/deed-transfer' },
          ],
        },
        {
          title: 'Additional Services',
          links: [
            { name: 'Virtual Mail', path: '/services/virtual-mail' },
          ],
        },
      ],
    },
    attorneys: {
      title: 'Attorneys',
      sections: [
        {
          title: 'Business Legal',
          links: [
            { name: 'Business Attorney Plans', path: '/services/business-attorney' },
          ],
        },
        {
          title: 'Personal Legal',
          links: [
            { name: 'Personal Attorney Plans', path: '/services/personal-attorney' },
          ],
        },
        {
          title: 'Intellectual Property',
          links: [
            { name: 'Trademark Registration (Attorney-Led)', path: '/services/trademark-attorney' },
          ],
        },
        {
          title: 'General Legal',
          links: [
            { name: 'Other Legal Matters', path: '/services/other-legal' },
          ],
        },
      ],
    },
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="/logo-black.png" alt="Legal Flick" className="h-10 w-auto" />
            </Link>

            {/* Desktop Navigation */}
            {showNav && (
              <nav className="hidden lg:flex items-center space-x-8">
                <Link to="/about" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  About
                </Link>
                {Object.keys(megaMenuData).map((key) => (
                  <div
                    key={key}
                    className="relative"
                    onMouseEnter={() => {
                      if (closeTimeout) {
                        clearTimeout(closeTimeout);
                        setCloseTimeout(null);
                      }
                      setActiveMegaMenu(key);
                    }}
                    onMouseLeave={() => {
                      const timeout = setTimeout(() => {
                        setActiveMegaMenu(null);
                      }, 300);
                      setCloseTimeout(timeout);
                    }}
                  >
                    <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors py-2">
                      <span className="font-medium">{megaMenuData[key].title}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                  Contact
                </Link>
              </nav>
            )}

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => setIsModalOpen(true)}
              >
                Get Legal Help
              </Button>
              {location.pathname === '/lp' && onChatClick && (
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={onChatClick}
                >
                  Chat Now
                </Button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mega Menu Dropdown */}
        {activeMegaMenu && showNav && (
          <div
            className="absolute left-0 right-0 bg-white border-t border-gray-200 shadow-lg"
            onMouseEnter={() => {
              if (closeTimeout) {
                clearTimeout(closeTimeout);
                setCloseTimeout(null);
              }
            }}
            onMouseLeave={() => {
              const timeout = setTimeout(() => {
                setActiveMegaMenu(null);
              }, 300);
              setCloseTimeout(timeout);
            }}
          >
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${
              activeMegaMenu === 'attorneys' ? 'py-6' : 'py-8'
            }`}>
              <div className={`grid ${
                activeMegaMenu === 'personal' ? 'grid-cols-4 gap-8' : 
                activeMegaMenu === 'attorneys' ? 'grid-cols-4 gap-8 max-w-5xl mx-auto' : 
                'grid-cols-3 gap-8'
              }`}>
                {megaMenuData[activeMegaMenu].sections.map((section, idx) => (
                  <div key={idx}>
                    {section.title && <h3 className="font-semibold text-gray-900 mb-3">{section.title}</h3>}
                    <ul className={activeMegaMenu === 'attorneys' ? 'space-y-2' : 'space-y-2'}>
                      {section.links.map((link, linkIdx) => (
                        <li key={linkIdx}>
                          <Link
                            to={link.path}
                            className="text-gray-600 hover:text-blue-600 transition-colors text-sm"
                          >
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && showNav && (
          <div className="lg:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-4 space-y-2">
              <Link 
                to="/about" 
                className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              
              {Object.keys(megaMenuData).map((key) => (
                <div key={key} className="border-b border-gray-200">
                  <button
                    className="w-full flex items-center justify-between py-3 text-gray-900 font-semibold"
                    onClick={() => setMobileExpandedMenu(mobileExpandedMenu === key ? null : key)}
                  >
                    <span>{megaMenuData[key].title}</span>
                    <ChevronDown 
                      className={`w-5 h-5 transition-transform ${
                        mobileExpandedMenu === key ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {mobileExpandedMenu === key && (
                    <div className="pb-3 space-y-3">
                      {megaMenuData[key].sections.map((section, idx) => (
                        <div key={idx} className="ml-2">
                          {section.title && (
                            <div className="font-medium text-gray-700 text-sm mb-2">
                              {section.title}
                            </div>
                          )}
                          <ul className="space-y-2 ml-2">
                            {section.links.map((link, linkIdx) => (
                              <li key={linkIdx}>
                                <Link 
                                  to={link.path} 
                                  className="text-gray-600 text-sm hover:text-blue-600 block py-1"
                                  onClick={() => {
                                    setMobileMenuOpen(false);
                                    setMobileExpandedMenu(null);
                                  }}
                                >
                                  {link.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              <Link 
                to="/contact" 
                className="block py-2 text-gray-700 hover:text-blue-600 font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
              
              <div className="pt-4 space-y-2">
                <Button
                  variant="outline"
                  className="w-full border-blue-600 text-blue-600"
                  onClick={() => {
                    setIsModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                >
                  Get Legal Help
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Header;